import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContractWiserLogo } from "@/components/ContractWiserLogo";

export default function TermsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Terms of Service | ContractWiser";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <ContractWiserLogo size="default" />
          </button>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-eucalypt-700">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-eucalypt-800 mb-8">
              Terms of Service
            </h1>
            
            <div className="prose prose-eucalypt max-w-none space-y-6 text-muted-foreground">
              <p className="text-sm">Last updated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">1. Acceptance of Terms</h2>
                <p>By accessing or using ContractWiser ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">2. Service Description</h2>
                <p>ContractWiser is an automated document analysis tool that provides <strong>educational summaries only</strong> of property contracts. The Service uses artificial intelligence to extract and summarize information from uploaded documents.</p>
                <p className="mt-4"><strong>THE SERVICE DOES NOT PROVIDE LEGAL ADVICE.</strong> The information generated is for general educational and informational purposes only and should not be construed as legal advice, professional advice, or a substitute for consultation with qualified legal professionals.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">3. No Solicitor-Client Relationship</h2>
                <p>Use of the Service does not create a solicitor-client, attorney-client, or any professional-client relationship between you and ContractWiser, its owners, operators, or affiliates. You acknowledge that:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>ContractWiser is not a law firm and does not provide legal services</li>
                  <li>No licensed legal professional reviews the output generated by the Service</li>
                  <li>The Service cannot and does not provide advice tailored to your specific circumstances</li>
                  <li>You should always seek independent legal advice from a qualified solicitor or conveyancer before signing any property contract</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">4. Disclaimer of Warranties</h2>
                <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>Warranties of merchantability or fitness for a particular purpose</li>
                  <li>Warranties of accuracy, completeness, or reliability of any analysis or summary</li>
                  <li>Warranties that the Service will be uninterrupted, timely, secure, or error-free</li>
                  <li>Warranties regarding the accuracy of AI-generated content</li>
                </ul>
                <p className="mt-4">The automated analysis may contain errors, omissions, or inaccuracies. You acknowledge that AI technology has limitations and may not correctly interpret all document content, particularly handwritten notes, unusual formatting, or complex legal provisions.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">5. Limitation of Liability</h2>
                <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, CONTRACTWISER, ITS OWNERS, OPERATORS, AFFILIATES, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY:</strong></p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>Direct, indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, revenue, data, or business opportunities</li>
                  <li>Property damage or personal injury</li>
                  <li>Damages arising from your reliance on any information provided by the Service</li>
                  <li>Damages arising from decisions made based on the Service output</li>
                  <li>Damages arising from errors, omissions, or inaccuracies in the analysis</li>
                  <li>Damages arising from unauthorized access to your data</li>
                </ul>
                <p className="mt-4">This limitation applies regardless of the legal theory upon which damages are sought, whether in contract, tort (including negligence), strict liability, or otherwise, even if ContractWiser has been advised of the possibility of such damages.</p>
                <p className="mt-4"><strong>IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID FOR THE SPECIFIC SERVICE THAT GAVE RISE TO THE CLAIM.</strong></p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">6. Indemnification</h2>
                <p>You agree to indemnify, defend, and hold harmless ContractWiser, its owners, operators, affiliates, employees, and agents from and against any and all claims, damages, losses, costs, and expenses (including reasonable legal fees) arising from:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>Your use of the Service</li>
                  <li>Your reliance on any information provided by the Service</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any rights of another party</li>
                  <li>Any decisions you make based on the Service output</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">7. User Responsibilities</h2>
                <p>By using the Service, you acknowledge and agree that:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>You are solely responsible for verifying all information in any contract you sign</li>
                  <li>You will seek independent legal advice before making any property purchase decisions</li>
                  <li>You will not rely solely on the Service output when making legal or financial decisions</li>
                  <li>You have the legal right to upload any documents you submit to the Service</li>
                  <li>You are at least 18 years of age</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">8. Payment and Refunds</h2>
                <p>Payment is required before analysis is performed. All sales are final. Refunds may be issued at our sole discretion only in cases where the Service fails to generate any output due to technical errors on our part.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">9. Intellectual Property</h2>
                <p>The Service, including its design, features, and content (excluding user-uploaded documents), is owned by ContractWiser and protected by intellectual property laws. You retain ownership of documents you upload.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">10. Governing Law</h2>
                <p>These Terms shall be governed by and construed in accordance with the laws of New South Wales, Australia. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of New South Wales.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">11. Changes to Terms</h2>
                <p>We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the modified Terms.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">12. Contact</h2>
                <p>For questions about these Terms, contact us at: <a href="mailto:admin@contractwiser.com.au" className="text-eucalypt-700 hover:underline">admin@contractwiser.com.au</a></p>
              </section>

              <div className="mt-12 p-6 bg-terracotta-50 border-l-4 border-terracotta-500">
                <p className="font-semibold text-eucalypt-800">Important Reminder</p>
                <p className="mt-2">By using ContractWiser, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree, please do not use the Service.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
